import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Download, Eye, Gift, Layers } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const easeOut = [0.23, 1, 0.32, 1] as const;
const heroHighlights = [{
  label: '6 Complete Volumes',
  Icon: Layers
}, {
  label: '150 Ready-to-Teach Lessons',
  Icon: BookOpen
}, {
  label: '100% Visual Core Teaching Material',
  Icon: Eye
}, {
  label: '5 Professional Bonus Guides',
  Icon: Gift
}, {
  label: 'Immediate Digital Access',
  Icon: Download
}];
export function Hero() {
  return <section className="relative pt-6 sm:pt-10 pb-12 px-4 overflow-hidden bg-primary tech-grid" data-id="element-112" data-mp="s9ggrzn38g86">
      <div className="max-w-5xl mx-auto flex flex-col items-center text-center" data-id="element-113" data-mp="nbygexn4z9bs">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOut
      }} className="w-full" data-id="element-114" data-mp="3d2bhzk51cpsw">
          <div className="inline-block px-3 py-1 rounded-md border border-accent/60 bg-white/5 text-accent font-semibold text-[10px] sm:text-sm tracking-wider whitespace-nowrap mb-4 sm:mb-6" data-id="element-115" data-mp="3j9u06x1wdkkr">
            AUTOMOTIVE TECHNOLOGY INSTRUCTOR READY
          </div>

          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold uppercase leading-tight mb-4 sm:mb-6 text-white" data-id="element-116" data-mp="3e9625rf943h1">
            Solve Months of Automotive Lesson Planning in Just{' '}
            <span className="text-accent" data-id="element-117" data-mp="3599f1u8ifco3">5 MINUTES</span>
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-white/80 max-w-3xl mx-auto mb-4 sm:mb-6" data-id="element-118" data-mp="2qjv8etwbj9ce">
            Automotive Technology Instructor Ready —{' '}
            <span className="text-accent font-semibold" data-id="element-119" data-mp="2vjg50xo0n5ly">
              150 Ready-to-Teach Automotive Technology Lessons
            </span>
            , 6 Complete Volumes and{' '}
            <span className="text-accent font-semibold" data-id="element-120" data-mp="2ljpbozb6ywng">5 Professional Bonus Guides</span>
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: easeOut
      }} className="relative w-full max-w-[320px] sm:max-w-lg md:max-w-3xl mb-4 sm:mb-6" data-id="element-121" data-mp="s3ck463mvbog">
          <img src={HERO_IMAGE} alt="Automotive Technology Instructor Ready complete system: 6 volumes, 150 ready-to-teach lessons and 5 professional bonus guides" className="relative z-10 w-full h-auto drop-shadow-[0_0_48px_rgb(var(--color-secondary)/0.35)]" data-id="element-122" data-mp="degdmdh866py" />
          <div className="absolute inset-x-[10%] -bottom-2 h-6 rounded-[50%] bg-black/40 blur-xl" aria-hidden="true" data-id="element-123" data-mp="11152xk8kd1gl" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.2,
        ease: easeOut
      }} className="w-full" data-id="element-124" data-mp="3lt740mm25js5">
          <p className="text-sm sm:text-lg md:text-xl text-white/85 leading-snug max-w-2xl mx-auto mb-3 sm:mb-4" data-id="element-125" data-mp="1xam6ou2m9x0i">
            The complete <span className="text-accent font-semibold" data-id="element-126" data-mp="218to7r4wn2cr">6-in-1</span> Automotive
            Technology teaching system with{' '}
            <span className="text-accent font-semibold" data-id="element-127" data-mp="2b0fvjce4gha8">150 ready-to-teach lessons</span>,{' '}
            <span className="text-accent font-semibold" data-id="element-128" data-mp="2fzsswr2md6r2">100% visual</span> core teaching material
            and 5 professional bonus guides.
          </p>
          <p className="text-sm sm:text-base text-white/70 leading-snug max-w-2xl mx-auto mb-5 sm:mb-7" data-id="element-129" data-mp="2kxc5znnkzdhf">
            Created to help Automotive Technology instructors spend less time planning, explain
            complex vehicle systems more clearly and walk into the classroom or workshop with
            structured material already prepared.
          </p>

          <div className="flex justify-center mb-5 sm:mb-7" data-id="element-130" data-mp="zl0yk8lwxpf4">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg md:text-xl py-3 sm:py-5 px-5 sm:px-10" data-id="element-131" data-mp="znn8ug06uj11">
              GET THE COMPLETE AUTOMOTIVE TEACHING SYSTEM NOW
            </Button>
          </div>

          <ul className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm" data-id="element-132" data-mp="e8sbpb0h0qet">
            {heroHighlights.map(({
            label,
            Icon
          }) => <li key={label} className="flex items-center gap-1.5 bg-white text-primary font-semibold border border-accent rounded-md px-2.5 py-1.5" data-id="element-133" data-mp="3hpqyvtz7kuxi">
                <Icon size={14} className="text-secondary shrink-0" data-id="element-134" data-mp="3rgllx0ka3yk0" />
                <span data-id="element-135" data-mp="3mpdfmde8wtb2">{label}</span>
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}